/**
 * QUANTUM TERMINAL — single source of design tokens.
 *
 * Replaces the five parallel sources found in the audit (§4.1):
 *   A: theme/glasswork.js (THEME), theme/glasswork.css (--gw-*), theme/obsidian.css (--color-*)
 *   B: shared/theme.ts (theme object), Design/tokens.css
 *
 * Rules enforced here:
 *  - Every colour is an opaque hex unless it is deliberately a wash/overlay (audit §4.2 note 2:
 *    alpha-on-white text "floats" over flat s1/s2/s3, so text is solid hex).
 *  - JS reads these objects; CSS reads the variables emitted by cssVars(). No third copy.
 *  - Nothing in a component may hardcode a colour. Light theme later = one extra block below.
 *
 * Emission: `writeFileSync('tokens.css', cssVars())` in a prebuild step, or inject at boot.
 *
 * Palette: Graphite — owner decision 27.09.2026 (docs/TERMINAL_REDESIGN_PLAN.md §7.2).
 */

/* ─────────────────────────────────────────────────────────────── PALETTE ── */

export const palette = {
  /* Graphite palette — owner decision 27.09.2026 (docs/TERMINAL_REDESIGN_PLAN.md §7.2). */
  void: '#0C0F13', // window background, behind all panels
  bull: '#3DDC97', // price up / bid / long
  bear: '#F2607F', // price down / ask / short
  warn: '#F5B454', // attention, alerts, stop orders
  violet: '#9A8CFF', // ideas / situations / AI-derived content
  info: '#5CB8FF', // neutral data series (chart indicator lines)

  /* Surfaces — flat and opaque. cTrader/Tiger model, not glass (audit §4.5 "conflict of form"). */
  s1: '#12161B', // dock background / gutters between panels
  s2: '#171C22', // panel body
  s3: '#1E242B', // nested card, input, table header
  s4: '#262D35', // hover row / selected row

  /* Lines — 1px, three weights. */
  line: '#222830', // hairline inside a panel (table rules)
  line2: '#2B323B', // panel border, control border
  line3: '#3A424D', // emphasised border: focus-adjacent, splitter hover

  /* Text — solid hex on every surface. */
  hi: '#E9ECEF', // primary: numbers, values
  mid: '#9BA4AE', // secondary: labels, column heads
  low: '#656E79', // tertiary: units, timestamps — never for body copy
  dis: '#3D444D', // disabled

  acc: '#3DDC97',
  accHi: '#5FE6AC', // hover / active text on accent
  accDeep: '#1D3B35', // accent-tinted fill for selected rows

  /* logo + glows only — the palette's own accent is `acc`. */
  brand: '#2CF5A7',

  /* Liquidation — deliberately NOT bear. Bear is "the market went down";
     liquidation is "you are about to be gone". Hotter and darker. */
  liq: '#FF3A2E',

  /* Structural levels — quiet by design: they are context, not signal. */
  level: '#5A6573',

  /* Deep fills (badge/chip backgrounds). */
  bullDeep: '#1D3B35',
  bearDeep: '#3A2731',
  warnDeep: '#3B342A',
  violetDeep: '#2C2E45',
  accDeepBg: '#1D3B35',
  neutralDeep: '#2B323B',

  /* text colour to sit ON a filled accent / bull / bear surface */
  onAcc: '#04130C',
  onBull: '#04130C',
  onBear: '#2A0410',
} as const;

/** Light theme — overrides the raw palette + washes + shadows only; the
 *  trading semantics and on-* colours resolve through var() and follow.
 *  Emitted into `tokens.css` under `[data-theme='light']`. Contrast on the
 *  light `--s2` is AA for hi/mid/acc; bull/bear/warn are darkened for text. */
export const lightPalette = {
  void: '#E9EDF3', s1: '#FFFFFF', s2: '#F7F9FC', s3: '#EDF1F7', s4: '#E0E7F1',
  line: '#E4E8F0', line2: '#D4DBE6', line3: '#B4BFCF',
  hi: '#121722', mid: '#566072', low: '#8A94A6', dis: '#B4BFCF',
  acc: '#2E8B57', accHi: '#37A468', accDeep: '#DCEDE3',
  brand: '#2E8B57',
  liq: '#D62A1C', level: '#7C8AA3',
  bull: '#0C9E60', bear: '#DA2F63', warn: '#B9760E', violet: '#6A57DE', info: '#0B7FB5',
  bullDeep: '#D6F1E4', bearDeep: '#FADCE6', warnDeep: '#FAECD1', violetDeep: '#E4E1FA', neutralDeep: '#E4E8F0',
  onAcc: '#FFFFFF', onBull: '#FFFFFF', onBear: '#FFFFFF',
} as const;

export const lightWash = {
  bull: 'rgba(12,158,96,.14)', bear: 'rgba(218,47,99,.14)',
  bullStrong: 'rgba(12,158,96,.26)', bearStrong: 'rgba(218,47,99,.26)',
  warn: 'rgba(185,118,14,.16)', acc: 'rgba(46,139,87,.12)', accBorder: 'rgba(46,139,87,.5)',
  violet: 'rgba(106,87,222,.14)', liq: 'rgba(214,42,28,.16)',
  hover: 'rgba(20,40,90,.05)', scrim: 'rgba(20,28,40,.38)',
} as const;

/** Alpha washes. The only place rgba() is legal: depth bars, flashes, focus rings. */
export const wash = {
  bull: 'rgba(61,220,151,.14)',
  bear: 'rgba(242,96,127,.14)',
  bullStrong: 'rgba(61,220,151,.28)',
  bearStrong: 'rgba(242,96,127,.28)',
  warn: 'rgba(245,180,84,.14)',
  acc: 'rgba(61,220,151,.12)',
  accBorder: 'rgba(61,220,151,.45)',
  violet: 'rgba(154,140,255,.14)',
  liq: 'rgba(255,58,46,.18)',
  hover: 'rgba(233,236,239,.04)',
  scrim: 'rgba(6,8,11,.55)', // modal backdrop
  zebra: 'rgba(255,255,255,.025)',
} as const;

/* ────────────────────────────────────────────────── TRADING SEMANTICS ── */
/** Every trading concept resolves to a token. No ad-hoc hex at call sites (task §3). */
export const trading = {
  bid: palette.bull,
  ask: palette.bear,
  bidDepth: wash.bull,
  askDepth: wash.bear,
  long: palette.bull,
  short: palette.bear,
  pnlPos: palette.bull,
  pnlNeg: palette.bear,
  pnlFlat: palette.mid,
  numBull: '#57DEA4',
  numBear: '#F17590',
  ordLimit: palette.acc, // resting, passive → accent (machine state, not direction)
  ordMarket: palette.hi, // immediate → neutral bright
  ordStop: palette.warn, // conditional → amber
  lineEntry: palette.hi,
  lineSl: palette.bear,
  lineTp: palette.bull,
  lineLiq: palette.liq,
  level: palette.level,
  situation: palette.violet,
  alert: palette.warn,
  connOk: palette.bull,
  connSlow: palette.warn, // reconnecting / stale
  connDown: palette.bear,
} as const;

/* ───────────────────────────────────────────────────────── TYPOGRAPHY ── */
/** Inter + JetBrains Mono, self-hosted (task: no Google CDN). Audit §4.4: both
 *  projects already run Inter; A's Geist declaration was dead. */
export const typography = {
  fontUi: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
  fontMono: "'JetBrains Mono', ui-monospace, 'SF Mono', monospace",
  /* 12px base is B's density (audit §4.4) and the floor for an 8-panel layout. */
  size: {
    micro: 'calc(10px * var(--fs-scale))', xs: 'calc(11px * var(--fs-scale))',
    sm: 'calc(12px * var(--fs-scale))', base: 'calc(13px * var(--fs-scale))',
    md: 'calc(14px * var(--fs-scale))', lg: 'calc(16px * var(--fs-scale))',
    xl: 'calc(20px * var(--fs-scale))', xxl: 'calc(26px * var(--fs-scale))',
  },
  weight: { regular: 400, medium: 500, semi: 600, bold: 700 },
  lh: { tight: 1.15, base: 1.4, relaxed: 1.55 },
  /* .lbl — micro caps label, ported from B tokens.css */
  labelTracking: '.06em',
  numTracking: '-.02em',
} as const;

/* ──────────────────────────────────────────────── SPACING / DENSITY ── */
/** Two density modes (audit §8 q6 → answered: give the switch, default compact). */
export const density = {
  compact: {
    sp0: '0px', sp1: '2px', sp2: '4px', sp3: '6px', sp4: '8px', sp5: '12px', sp6: '16px', sp7: '24px',
    rowLadder: '18px',
    rowTape: '16px',
    rowTable: '22px',
    rowJournal: '24px',
    panelHeader: '24px',
    tabH: '24px',
    ctrlSm: '20px',
    ctrlMd: '24px',
    titlebar: '32px',
    statusbar: '22px',
  },
  medium: {
    sp0: '0px', sp1: '3px', sp2: '6px', sp3: '8px', sp4: '12px', sp5: '16px', sp6: '20px', sp7: '32px',
    rowLadder: '20px',
    rowTape: '18px',
    rowTable: '26px',
    rowJournal: '30px',
    panelHeader: '28px',
    tabH: '28px',
    ctrlSm: '24px',
    ctrlMd: '28px',
    titlebar: '36px',
    statusbar: '24px',
  },
  comfortable: {
    sp0: '0px', sp1: '3px', sp2: '6px', sp3: '8px', sp4: '12px', sp5: '16px', sp6: '20px', sp7: '32px',
    rowLadder: '22px',
    rowTape: '20px',
    rowTable: '28px',
    rowJournal: '30px',
    panelHeader: '30px',
    tabH: '30px',
    ctrlSm: '26px', // ≥24px hit target requirement holds in comfortable
    ctrlMd: '30px',
    titlebar: '38px',
    statusbar: '26px',
  },
} as const;

/* ─────────────────────────────────────────────── SHAPE / SHADOW / Z ── */
export const radius = {
  ctrl: '6px',
  input: '6px',
  panel: '6px',
  modal: '20px',
  pill: '999px',
} as const;

/** Shadows exist ONLY on floating layers. Docked panels are flat 1px — a shadow on
 *  eight tiled panels is both wrong (fake depth in a tiling layout) and GPU-expensive. */
export const shadow = {
  float: '0 24px 60px rgba(0,0,0,.55)',
  dropdown: '0 12px 30px rgba(0,0,0,.5)',
  ghost: '0 20px 44px rgba(0,0,0,.6)',
  none: 'none',
} as const;

export const blurFloat = 'blur(16px)'; // modals, palette, tooltips, ctx menu, ghost, popout preview only

export const motion = {
  durPress: '80ms',
  durFast: '120ms', // hover, press, tab underline
  durBase: '180ms', // panel open, dropdown, toast in
  durSlow: '240ms', // modal in, popout preview
  durSlower: '240ms', // §7.18 "soft" — no overshoot delay beyond the modal itself
  easeOut: 'cubic-bezier(.2,.7,.3,1)',
  /* The spring lives on FLOATING surfaces (modal, toast, dropdown, ticket) — the
     site's motion is the canon for those. `easeWin` keeps the same curve under
     its own name for the one profit-close celebration. */
  easeSpring: 'cubic-bezier(.3,1.36,.44,1)',
  easeWin: 'cubic-bezier(.3,1.36,.44,1)',
} as const;

export const z = {
  panel: 1,
  overlay: 100, // chart overlays, ladder order overlay
  dropdown: 200, // select, context menu, tooltip
  modal: 300,
  toast: 400,
  dragghost: 500,
} as const;

/* ─────────────────────────────────────────────────────────── EMITTER ── */

const kebab = (s: string) => s.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase());

function block(map: Record<string, string | number>, prefix: string): string {
  return Object.entries(map).map(([k, v]) => `  --${prefix}${kebab(k)}: ${v};`).join('\n');
}

/** Single emitter. Everything CSS knows about colour comes from here. */
export function cssVars(): string {
  return `/* GENERATED FROM tokens.ts — do not edit by hand */
:root {
${block(palette, '')}
${block(wash, 'w-')}
${block(trading, 't-')}
  --font-ui: ${typography.fontUi};
  --font-mono: ${typography.fontMono};
${block(typography.size, 'fs-')}
${block(typography.lh, 'lh-')}
${block(radius, 'r-')}
${block(shadow, 'shadow-')}
  --blur-float: ${blurFloat};
${block(motion, '')}
${block(z, 'z-')}
}
:root {
${block(density.medium, '')}
}
[data-density='compact'] {
${block(density.compact, '')}
}
[data-density='comfortable'] {
${block(density.comfortable, '')}
}
/* Light theme — see lightPalette / lightWash above. Hand-maintained in
   tokens.css to keep the diff readable; keep the two in sync. */
[data-theme='light'] {
${block(lightPalette, '')}
${block(lightWash, 'w-')}
}
@media (prefers-reduced-motion: reduce) {
  :root {
    --dur-press: 1ms; --dur-fast: 1ms; --dur-base: 1ms; --dur-slow: 1ms; --dur-slower: 1ms;
    --ease-win: linear; --ease-spring: linear;
  }
}`;
}

export type Density = keyof typeof density;
export const tokens = { palette, wash, trading, typography, density, radius, shadow, motion, z, blurFloat };
export default tokens;
